import React, { useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import {
    XYPlot,
    CustomSVGSeries,
    LineSeries,
    Hint,
} from 'react-vis'
import '../../../node_modules/react-vis/dist/style.css'
import { DESKTOP, TABLET, MEDIUM, ULTRA_LIGHT, REGULAR } from '@streamr/streamr-layout/styled'
import formatMoney from '~utils/formatMoney'

const StyledXYPlot = styled(XYPlot)`
    display: none;

    @media (min-width: ${DESKTOP}px) {
        display: block;
    }
`

const StyledHintContent = styled.div`
    width: 80px;
    transform: translate(-39px, 10px);
    text-align: center;
    font-size: 12px;
    font-weight: ${REGULAR};
    line-height: 1.25;

    #chart line {
        stroke: #B1B1B1;
        opacity: 1;
        stroke-width: 2px;
    }

    svg#chart {
        display: block;
        margin: 0 auto;
        background: transparent;
    }

    .chartXVal,
    .chartYVal {
        display: block;
        text-shadow: 0 1px #000000;
    }

    .chartXVal {
        color: rgba(0, 0, 0, 0.5);
    }
`

const PlotPlaceholder = styled.div`
    display: none;

    @media (min-width: ${DESKTOP}px) {
        display: block;
        height: 260px;
        width: 484px;
    }
`

const Chart = ({ data, dates }) => {
    const [value, setValue] = useState(null)

    const lastDataPoint = data ? [
        {
            ...data[data.length - 1],
            size: 14,
            style: {
                stroke: '#FF5C00',
                fill: '#FF5C00',
            },
        },
    ] : undefined

    return data[0] == null ? (
        <PlotPlaceholder />
    ) : (
        <StyledXYPlot
            height={260}
            width={484}
            margin={{
                left: 0,
                right: 10,
                top: 72,
                bottom: 20,
            }}
            onMouseLeave={() => setValue(null)}
        >
            <CustomSVGSeries
                data={lastDataPoint}
            />
            <LineSeries
                data={data}
                onNearestX={(datapoint) => (setValue(datapoint))}
                color={!value ? '#FF5C00' : 'rgba(255, 92, 0, 0.7)'}
                strokeStyle="solid"
                strokeWidth="4"
            />
            {!!value
            && (
                <Hint
                    value={value}
                    align={{
                        horizontal: 'right',
                        vertical: 'bottom',
                    }}
                >
                    <StyledHintContent>
                        <svg id="chart" width="1" height="14">
                            <line
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="10"
                            />
                        </svg>
                        <span className="chartXVal">{dates[value.x]}</span>
                        <span className="chartYVal">{value.y}</span>
                    </StyledHintContent>
                </Hint>
            )}
        </StyledXYPlot>
    )
}

const MetricTile = styled.div`
    & + & {
        margin-top: 72px;
    }

    @media (min-width: ${TABLET}px) {
        & + & {
            margin-top: 100px;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        width: 484px;

        & + & {
            margin-top: 0;
        }
    }
`

const List = styled.div`
    @media (min-width: ${TABLET}px) {
        margin: 0 auto;
        width: 512px;
    }

    @media (min-width: ${DESKTOP}px) {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        margin: -10rem 0 0;
        width: auto;

        ${MetricTile} {
            margin-top: 10rem;
        }
    }
`

const UnstyledValue = ({ children, ...props }) => (
    <div {...props}>
        {children >= 10000 ? (
            formatMoney(`${children}`).replace(/\$/, '').split(/\s+/).map((p, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={i}>
                    {p}
                </span>
            ))
        ) : `${children || '0'}`}
    </div>
)

const Value = styled(UnstyledValue)`
    font-size: 26px;
    font-weight: ${ULTRA_LIGHT};
    letter-spacing: -0.65px;

    span + span {
        margin-left: 0.125em;
    }

    @media (min-width: ${TABLET}px) {
        letter-spacing: -1px;
        font-size: 40px;
    }
`

const animation = keyframes`
    0% {
        left: 0%;
        right: 100%;
    }

    33% {
        left: 0%;
        right: 0%;
    }

    67% {
        left: 0%;
        right: 0%;
    }

    100% {
        left: 100%;
        right: 0%;
    }
`
const loadingCss = css`
    color: rgba(12, 0, 154, 0.5);

    &::after {
        content: '';
        display: block;
        height: 1px;
        background: rgba(255, 255, 255, 0.5);
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 100%;
        animation: 1200ms infinite;
        animation-name: ${animation};
        will-change: left, right;
    }
`

const UnstyledHeadling = ({ title, value, loading, ...props }) => (
    <div {...props} css={!!loading && loadingCss}>
        {title}
        <Value>
            {(value[value.length - 1] || {}).y}
        </Value>
    </div>
)

const Header = styled(UnstyledHeadling)`
    align-items: baseline;
    border-bottom: 1px solid rgba(12, 0, 154, 0.7);
    display: flex;
    position: relative;
    color: #0C009A;

    h4 {
        color: #0C009A;
        flex-grow: 1;
        font-size: 12px;
        font-weight: ${MEDIUM};
        letter-spacing: 1.5px;
        line-height: 1em;
        margin: 0 0 10px;
        text-transform: uppercase;
    }

    @media (min-width: ${TABLET}px) {
        h4 {
            font-size: 14px;
            letter-spacing: 2px;
        }
    }
`

Object.assign(MetricTile, {
    Chart,
    Header,
    List,
})

export default MetricTile
